import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  NotificationPresetDto,
  NotificationPresetTrigger,
  NotificationPresetTriggerCategory,
  NotificationPresetVariant,
  NotificationPresetVenuePresence,
  NOTIFICATION_PRESET_TRIGGER_CATEGORY_DESCRIPTIONS,
  getNotificationPresetCategoryAnchorId,
  getNotificationPresetTriggersByCategory,
  isImmediateNotificationPresetTrigger,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import NotificationPresetModal from '../../components/modals/NotificationPresetModal'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import Icon from '../../components/Icon'
import { adminService } from '../../services/admin.service'

type PresetTableRow = NotificationPresetDto & {
  delayLabel: string
  enabledLabel: string
  logSendsLabel: string
  variantLabel: string
  venuePresenceLabel: string
}

type NotificationPresetMeta = {
  triggers: NotificationPresetTrigger[]
  variants: NotificationPresetVariant[]
  triggerLabels: Record<string, string>
  triggerDescriptions: Record<string, string>
  triggerCategories: NotificationPresetTriggerCategory[]
  triggerCategoryLabels: Record<string, string>
  triggerToCategory: Record<string, NotificationPresetTriggerCategory>
  variantLabels: Record<string, string>
  venuePresenceValues?: NotificationPresetVenuePresence[]
  venuePresenceLabels?: Record<string, string>
  templateVariables: Record<string, string[]>
  templateVariablesByTrigger?: Record<string, string[]>
  defaultNotificationTypes?: Record<string, string>
  onboardingVariants?: NotificationPresetVariant[]
  venueVariants?: NotificationPresetVariant[]
  conversationFollowUpVariants?: NotificationPresetVariant[]
  deepLinkTargets?: string[]
  deepLinkTargetLabels?: Record<string, string>
  checkoutTypes?: string[]
  checkoutTypeLabels?: Record<string, string>
}

function formatDelay(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  if (minutes < 1440) {
    return `${Math.round(minutes / 60)} hr`
  }
  return `${Math.round(minutes / 1440)} day(s)`
}

function toTableRows(
  presets: NotificationPresetDto[],
  meta: NotificationPresetMeta | null,
): PresetTableRow[] {
  return presets.map(preset => ({
    ...preset,
    delayLabel: isImmediateNotificationPresetTrigger(preset.trigger) ? 'Immediate' : formatDelay(preset.delayMinutes),
    enabledLabel: preset.enabled ? 'Yes' : 'No',
    logSendsLabel: preset.shouldLogSends ? 'Yes' : 'No',
    variantLabel: meta?.variantLabels[preset.variant] ?? preset.variant,
    venuePresenceLabel: meta?.venuePresenceLabels?.[preset.venuePresence] ?? preset.venuePresence,
  }))
}

const PRESET_TABLE_COLUMNS = [
  { label: 'Name', accessor: 'name', type: 'string' },
  { label: 'Enabled', accessor: 'enabledLabel', type: 'string' },
  { label: 'Log sends', accessor: 'logSendsLabel', type: 'string' },
  { label: 'Variant', accessor: 'variantLabel', type: 'string' },
  { label: 'Venue presence', accessor: 'venuePresenceLabel', type: 'string' },
  { label: 'Delay', accessor: 'delayLabel', type: 'string' },
  { label: 'Sort', accessor: 'sortOrder', type: 'number' },
  { label: 'Priority', accessor: 'priority', type: 'number' },
  { label: 'Updated', accessor: 'updatedAt', type: 'datetime' },
] as const

function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set)
  if (next.has(item)) {
    next.delete(item)
  } else {
    next.add(item)
  }
  return next
}

const NotificationPresetsTab = () => {
  const [presets, setPresets] = useState<NotificationPresetDto[]>([])
  const [meta, setMeta] = useState<NotificationPresetMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingPreset, setEditingPreset] = useState<NotificationPresetDto | null>(null)
  const [deletingPreset, setDeletingPreset] = useState<NotificationPresetDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<NotificationPresetTriggerCategory>>(new Set())
  const [collapsedTriggers, setCollapsedTriggers] = useState<Set<NotificationPresetTrigger>>(new Set())
  const [collapseInitialized, setCollapseInitialized] = useState(false)

  const loadPresets = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [presetRows, presetMeta] = await Promise.all([
        adminService.getNotificationPresets(),
        adminService.getNotificationPresetMeta(),
      ])
      setPresets(presetRows)
      setMeta(presetMeta as NotificationPresetMeta)
    } catch {
      setError('Failed to load notification presets.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPresets()
  }, [loadPresets])

  const presetsByTrigger = useMemo(() => {
    const grouped = new Map<NotificationPresetTrigger, NotificationPresetDto[]>()
    for (const preset of presets) {
      const group = grouped.get(preset.trigger) ?? []
      group.push(preset)
      grouped.set(preset.trigger, group)
    }

    grouped.forEach(group => {
      group.sort((a: NotificationPresetDto, b: NotificationPresetDto) =>
        a.sortOrder - b.sortOrder || a.delayMinutes - b.delayMinutes || a.name.localeCompare(b.name),
      )
    })

    return grouped
  }, [presets])

  const triggerGroups = useMemo(() => getNotificationPresetTriggersByCategory(), [])

  const presetCountByCategory = useMemo(() => {
    const counts = new Map<NotificationPresetTriggerCategory, number>()
    for (const { category, triggers } of triggerGroups) {
      counts.set(
        category,
        triggers.reduce((total, trigger) => total + (presetsByTrigger.get(trigger)?.length ?? 0), 0),
      )
    }
    return counts
  }, [presetsByTrigger, triggerGroups])

  useEffect(() => {
    if (collapseInitialized || loading) {
      return
    }

    const emptyCategories = new Set<NotificationPresetTriggerCategory>()
    const emptyTriggers = new Set<NotificationPresetTrigger>()

    for (const { category, triggers } of triggerGroups) {
      if ((presetCountByCategory.get(category) ?? 0) === 0) {
        emptyCategories.add(category)
      }
      for (const trigger of triggers) {
        if ((presetsByTrigger.get(trigger)?.length ?? 0) === 0) {
          emptyTriggers.add(trigger)
        }
      }
    }

    setCollapsedCategories(emptyCategories)
    setCollapsedTriggers(emptyTriggers)
    setCollapseInitialized(true)
  }, [collapseInitialized, loading, presetCountByCategory, presetsByTrigger, triggerGroups])

  const handleExpandAll = () => {
    setCollapsedCategories(new Set())
    setCollapsedTriggers(new Set())
  }

  const handleCollapseAll = () => {
    setCollapsedCategories(new Set(triggerGroups.map(group => group.category)))
    setCollapsedTriggers(new Set(triggerGroups.flatMap(group => group.triggers)))
  }

  const scrollToCategory = useCallback((category: NotificationPresetTriggerCategory) => {
    setCollapsedCategories(current => {
      if (!current.has(category)) {
        return current
      }
      const next = new Set(current)
      next.delete(category)
      return next
    })

    window.setTimeout(() => {
      document.getElementById(getNotificationPresetCategoryAnchorId(category))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 0)
  }, [])

  useEffect(() => {
    if (loading || !collapseInitialized) {
      return
    }

    const hash = window.location.hash.slice(1)
    if (!hash.startsWith('notification-preset-category-')) {
      return
    }

    const category = hash.replace('notification-preset-category-', '') as NotificationPresetTriggerCategory
    if (!triggerGroups.some(group => group.category === category)) {
      return
    }

    scrollToCategory(category)
  }, [collapseInitialized, loading, scrollToCategory, triggerGroups])

  const handlePresetSaved = (saved: NotificationPresetDto) => {
    setPresets(prev => {
      const exists = prev.some(preset => preset.publicId === saved.publicId)
      if (exists) {
        return prev.map(preset => (preset.publicId === saved.publicId ? saved : preset))
      }
      return [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder || a.delayMinutes - b.delayMinutes)
    })

    const category = meta?.triggerToCategory[saved.trigger]
    if (category) {
      setCollapsedCategories(current => {
        if (!current.has(category)) {
          return current
        }
        const next = new Set(current)
        next.delete(category)
        return next
      })
    }
    setCollapsedTriggers(current => {
      if (!current.has(saved.trigger)) {
        return current
      }
      const next = new Set(current)
      next.delete(saved.trigger)
      return next
    })
  }

  const handlePresetDeleted = (publicId: string) => {
    setPresets(prev => prev.filter(preset => preset.publicId !== publicId))
    setEditingPreset(current => (current?.publicId === publicId ? null : current))
    setDeletingPreset(current => (current?.publicId === publicId ? null : current))
  }

  const handleConfirmDelete = async () => {
    if (!deletingPreset) {
      return
    }

    setIsDeleting(true)
    setError('')
    try {
      await adminService.deleteNotificationPreset(deletingPreset.publicId)
      handlePresetDeleted(deletingPreset.publicId)
    } catch {
      setError(`Failed to delete preset "${deletingPreset.name}".`)
    } finally {
      setIsDeleting(false)
    }
  }

  if (loading) {
    return <></>
  }

  return (
    <div className="notification-presets-page">
      <div className="notification-presets-overview card">
        <h3 className="notification-presets-overview__title">How presets are organized</h3>
        <p className="notification-presets-overview__intro">
          Presets are grouped by trigger category. Each category contains one or more triggers; within a trigger,
          higher-priority presets win when several match the same user. Use the links below to jump to a section.
        </p>
        <ul className="notification-presets-overview__list">
          {triggerGroups.map(({ category, categoryLabel, triggers }) => {
            const categoryPresetCount = presetCountByCategory.get(category) ?? 0

            return (
              <li key={category} className="notification-presets-overview__item">
                <a
                  href={`#${getNotificationPresetCategoryAnchorId(category)}`}
                  className="notification-presets-overview__link"
                  onClick={event => {
                    event.preventDefault()
                    window.history.replaceState(null, '', `#${getNotificationPresetCategoryAnchorId(category)}`)
                    scrollToCategory(category)
                  }}
                >
                  {categoryLabel}
                </a>
                <span className="notification-presets-overview__description">
                  {' — '}
                  {NOTIFICATION_PRESET_TRIGGER_CATEGORY_DESCRIPTIONS[category]}
                </span>
                <span className="notification-presets-overview__meta">
                  {categoryPresetCount} preset{categoryPresetCount === 1 ? '' : 's'},{' '}
                  {triggers.length} trigger{triggers.length === 1 ? '' : 's'}
                </span>
              </li>
            )
          })}
        </ul>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="notification-presets-toolbar">
        <div className="notification-presets-toolbar__secondary">
          <button type="button" className="styled-button sm" onClick={handleExpandAll}>
            <span>Expand all</span>
          </button>
          <button type="button" className="styled-button sm" onClick={handleCollapseAll}>
            <span>Collapse all</span>
          </button>
        </div>
        <button type="button" className="styled-button sm" onClick={() => setIsCreateModalOpen(true)}>
          <span>Add preset</span>
        </button>
      </div>

      {triggerGroups.map(({ category, categoryLabel, triggers }) => {
        const categoryPresetCount = presetCountByCategory.get(category) ?? 0
        const isCategoryCollapsed = collapsedCategories.has(category)

        return (
        <section
          key={category}
          id={getNotificationPresetCategoryAnchorId(category)}
          className={`notification-presets-category${isCategoryCollapsed ? ' is-collapsed' : ''}`}
        >
          <button
            type="button"
            className="notification-presets-collapse-header notification-presets-collapse-header--category"
            aria-expanded={!isCategoryCollapsed}
            onClick={() => setCollapsedCategories(current => toggleSetItem(current, category))}
          >
            <Icon
              name="CaretDown"
              className={`notification-presets-collapse-icon${isCategoryCollapsed ? ' is-collapsed' : ''}`}
            />
            <span className="notification-presets-collapse-title">{categoryLabel}</span>
            <span className="notification-presets-collapse-count">
              {categoryPresetCount} preset{categoryPresetCount === 1 ? '' : 's'}
            </span>
          </button>

          {!isCategoryCollapsed && triggers.map(trigger => {
            const triggerPresets = presetsByTrigger.get(trigger) ?? []
            const tableRows = toTableRows(triggerPresets, meta)
            const triggerLabel = meta?.triggerLabels[trigger] ?? trigger
            const triggerDescription = meta?.triggerDescriptions[trigger] ?? ''
            const isTriggerCollapsed = collapsedTriggers.has(trigger)

            return (
              <div
                key={trigger}
                className={`property-bar-field-group notification-presets-trigger-group${isTriggerCollapsed ? ' is-collapsed' : ''}`}
              >
                <button
                  type="button"
                  className="notification-presets-collapse-header notification-presets-collapse-header--trigger"
                  aria-expanded={!isTriggerCollapsed}
                  onClick={() => setCollapsedTriggers(current => toggleSetItem(current, trigger))}
                >
                  <Icon
                    name="CaretDown"
                    className={`notification-presets-collapse-icon${isTriggerCollapsed ? ' is-collapsed' : ''}`}
                  />
                  <span className="notification-presets-collapse-title">{triggerLabel}</span>
                  <span className="notification-presets-collapse-count">
                    {triggerPresets.length} preset{triggerPresets.length === 1 ? '' : 's'}
                  </span>
                </button>

                {!isTriggerCollapsed && (
                  <>
                    {triggerDescription && (
                      <p className="property-bar-field-group__description">{triggerDescription}</p>
                    )}

                    {triggerPresets.length > 0 ? (
                      <div className="roles-table-container">
                        <div className="table-wrapper">
                          <Table
                            id={`notification-presets-${trigger}`}
                            name={`${triggerLabel} presets`}
                            keyField="publicId"
                            initialSortColumn="sortOrder"
                            initialSortOrder="asc"
                            columns={[...PRESET_TABLE_COLUMNS]}
                            sourceData={tableRows}
                            isPropertyBarVisible={false}
                            filterTerms=""
                            disableGlobalSearch
                            onRowClick={(row: PresetTableRow) =>
                              setEditingPreset(presets.find(preset => preset.publicId === row.publicId) ?? null)
                            }
                            onDelete={(row: PresetTableRow) =>
                              setDeletingPreset(presets.find(preset => preset.publicId === row.publicId) ?? null)
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="roles-page-intro notification-presets-empty">No presets configured for this trigger.</p>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </section>
        )
      })}

      <NotificationPresetModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        preset={null}
        meta={meta}
        onSaved={handlePresetSaved}
      />

      <NotificationPresetModal
        isOpen={editingPreset !== null}
        onClose={() => setEditingPreset(null)}
        preset={editingPreset}
        meta={meta}
        onSaved={handlePresetSaved}
        onDeleted={handlePresetDeleted}
      />

      <ConfirmationModal
        isOpen={deletingPreset !== null}
        onClose={() => {
          if (!isDeleting) {
            setDeletingPreset(null)
          }
        }}
        onConfirm={handleConfirmDelete}
        title="Delete notification preset"
        confirmLabel="Delete"
        isLoading={isDeleting}
      >
        {deletingPreset
          ? `Delete "${deletingPreset.name}"? This cannot be undone.`
          : ''}
      </ConfirmationModal>
    </div>
  )
}

export default NotificationPresetsTab
